// chill-cart-drawer: chillandout cart drawer web component.
// Owns: open/close, focus trap, body lock, AJAX line ops, add-to-cart intercept.
// Server is source of truth — every operation re-renders via Section Rendering API.
//
// Element name: <cart-drawer> (different from Horizon's <cart-drawer-component>).
// Path: assets/chill-cart-drawer.js (Horizon ships its own at assets/cart-drawer.js;
// we don't share that file).
//
// Spec: docs/MASTER-BRIEF.md §10.5
// Budget: < 12kb gzip. No deps.

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TOAST_DURATION_MS = 4000;

// Snapshot the Shopify root path ONCE at module load — before any third-party
// script can mutate window.Shopify.routes.root. Reject anything that's not a
// strict relative path (defense against root being rewritten to an evil origin).
const CART_ROOT = (() => {
  const r = window.Shopify?.routes?.root ?? '/';
  if (typeof r !== 'string') return '/';
  // Must start with '/' and not '//' (which would be a protocol-relative URL).
  if (r === '/' || /^\/[^/]/.test(r)) return r;
  return '/';
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._isOpen = false;
    this._destroyed = false;
    this._previouslyFocused = null;
    this._opQueue = Promise.resolve();
    this._toastTimer = 0;
    this._sectionId = 'cart-drawer'; // overwritten by _refreshRefs from data-section-id="{{ section.id }}"

    // Bound handlers (so removeEventListener works).
    this._onKeydown = this._onKeydown.bind(this);
    this._onBackdropClick = this._onBackdropClick.bind(this);
    this._onCloseClick = this._onCloseClick.bind(this);
    this._onBodyClick = this._onBodyClick.bind(this);
    this._onCartOpen = this._onCartOpen.bind(this);
    this._onAddSubmit = this._onAddSubmit.bind(this);
  }

  connectedCallback() {
    // Refuse a second instance — site-wide singleton.
    const existing = document.querySelector('cart-drawer');
    if (existing && existing !== this) {
      console.warn('cart-drawer: a second instance was registered and will be inert. Remove the duplicate from your theme.');
      return;
    }

    this._destroyed = false;
    this._refreshRefs();
    this._wireEvents();
  }

  disconnectedCallback() {
    this._destroyed = true;
    if (this._isOpen) this._unlockBody();
    this._unwireEvents();
    if (this._toastTimer) clearTimeout(this._toastTimer);
  }

  _refreshRefs() {
    this._panel = this.querySelector('.cart-drawer__panel');
    this._backdrop = this.querySelector('[data-cart-drawer-backdrop]');
    this._closeBtn = this.querySelector('[data-cart-drawer-close]');
    this._body = this.querySelector('[data-cart-drawer-body]');
    // Read real Shopify section id passed from {{ section.id }} in Liquid.
    // Fallback to 'cart-drawer' so the GET /?sections= path renders the file standalone.
    this._sectionId = this.dataset.sectionId || 'cart-drawer';
  }

  _wireEvents() {
    this._backdrop?.addEventListener('click', this._onBackdropClick);
    this._closeBtn?.addEventListener('click', this._onCloseClick);
    // Delegated body-internal clicks (qty/remove/empty-state-cta) survive Section Rendering swaps.
    this._body?.addEventListener('click', this._onBodyClick);
    document.addEventListener('keydown', this._onKeydown);
    document.addEventListener('cart:open', this._onCartOpen);
    // Site-wide add-to-cart intercept — captures any /cart/add form.
    document.addEventListener('submit', this._onAddSubmit, true);
  }

  _unwireEvents() {
    this._backdrop?.removeEventListener('click', this._onBackdropClick);
    this._closeBtn?.removeEventListener('click', this._onCloseClick);
    this._body?.removeEventListener('click', this._onBodyClick);
    document.removeEventListener('keydown', this._onKeydown);
    document.removeEventListener('cart:open', this._onCartOpen);
    document.removeEventListener('submit', this._onAddSubmit, true);
  }

  /* ---------- public API ---------- */

  open() {
    if (this._isOpen || this._destroyed) return;
    this._isOpen = true;
    this._previouslyFocused = document.activeElement;
    this.classList.add('is-open');
    this._panel?.setAttribute('aria-hidden', 'false');
    // Inert siblings of cart-drawer for proper modal semantics on iOS VoiceOver.
    this._setSiblingsInert(true);
    this._lockBody();
    // Focus the close button after the slide-in animation has started.
    requestAnimationFrame(() => this._closeBtn?.focus());
    // Phase 2 polish: enhance Bridge slot with real complementary recommendations
    // (server-rendered first-eligible-from-collection is the fallback, kept on
    // network failure / no-bridge-collection / no-cart-items).
    this._enhanceBridge();
    // Phase 2 polish: wire mobile swipe-down-to-close on first open.
    this._setupSwipeDownToClose();
  }

  close() {
    if (!this._isOpen) return;
    this._isOpen = false;
    this.classList.remove('is-open');
    this._panel?.setAttribute('aria-hidden', 'true');
    this._setSiblingsInert(false);
    this._unlockBody();
    if (this._previouslyFocused && typeof this._previouslyFocused.focus === 'function') {
      this._previouslyFocused.focus();
    }
    this._previouslyFocused = null;
  }

  toggle() {
    this._isOpen ? this.close() : this.open();
  }

  /* ---------- event handlers ---------- */

  _onCartOpen() {
    this.open();
  }

  _onBackdropClick() {
    this.close();
  }

  _onCloseClick(e) {
    e.preventDefault();
    this.close();
  }

  _onKeydown(e) {
    if (!this._isOpen) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      this.close();
      return;
    }
    if (e.key === 'Tab') {
      this._handleTabTrap(e);
    }
  }

  _onBodyClick(e) {
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const line = removeBtn.closest('[data-cart-line]');
      if (line) this._enqueue(() => this._removeLine(line));
      return;
    }

    const qtyBtn = e.target.closest('[data-cart-qty-change]');
    if (qtyBtn) {
      e.preventDefault();
      const line = qtyBtn.closest('[data-cart-line]');
      if (!line) return;
      const delta = parseInt(qtyBtn.dataset.cartQtyChange, 10) || 0;
      const valueEl = line.querySelector('[data-cart-qty-value]');
      const current = parseInt(valueEl?.textContent || '0', 10);
      const next = Math.max(0, current + delta);
      this._enqueue(() => this._updateLine(line, next));
      return;
    }

    // Empty-state CTA closes the drawer (link still navigates).
    const closeOnNav = e.target.closest('[data-cart-drawer-close]');
    if (closeOnNav && closeOnNav.tagName === 'A') {
      this.close();
    }
  }

  _onAddSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.endsWith('/cart/add')) return;
    if (form.hasAttribute('data-cart-drawer-skip')) return;

    e.preventDefault();
    const formData = new FormData(form);
    this._enqueue(() => this._addToCart(formData));
  }

  /* ---------- AJAX queue ---------- */

  _enqueue(op) {
    this._opQueue = this._opQueue.then(() => op()).catch((err) => {
      console.error('cart-drawer op failed:', err);
      this._showToast(this.dataset.i18nError || "hmm, that didn't work. try again?");
    });
    return this._opQueue;
  }

  /* ---------- AJAX operations ---------- */

  async _updateLine(lineEl, quantity) {
    if (this._destroyed) return;
    this._setLoading(true);
    const key = lineEl.dataset.lineKey;
    const res = await fetch(`${CART_ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => null);
      throw new Error(errBody?.description || `cart/change failed: ${res.status}`);
    }
    await this._refreshDrawer();
    this._setLoading(false);
  }

  // Remove plays exit animation, then defers to _updateLine(0).
  // Phase 2 polish: captures undo state BEFORE removal so the toast can restore.
  async _removeLine(lineEl) {
    if (this._destroyed || !lineEl) return;
    const undoData = {
      variantId: lineEl.dataset.variantId,
      quantity: parseInt(lineEl.dataset.quantity, 10) || 1,
    };
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduced) {
      lineEl.classList.add('is-leaving');
      await new Promise((r) => setTimeout(r, 280));
    }
    await this._updateLine(lineEl, 0);
    if (undoData.variantId) {
      this._showToast(this.dataset.i18nRemoved || 'removed.', {
        actionLabel: this.dataset.i18nUndo || 'undo',
        onAction: () => this._enqueue(() => this._undoRemove(undoData)),
      });
    }
  }

  // Phase 2 polish: re-add the line item with the captured variant + quantity.
  async _undoRemove(undoData) {
    if (this._destroyed || !undoData.variantId) return;
    this._setLoading(true);
    const formData = new FormData();
    formData.set('id', undoData.variantId);
    formData.set('quantity', String(undoData.quantity));
    formData.set('sections', this._sectionId);
    const res = await fetch(`${CART_ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });
    if (!res.ok) {
      throw new Error(`undo add failed: ${res.status}`);
    }
    const json = await res.json();
    if (json.sections && json.sections[this._sectionId]) {
      this._swapBody(json.sections[this._sectionId], { markEntering: true });
    } else {
      await this._refreshDrawer();
    }
    this._setLoading(false);
    document.dispatchEvent(new CustomEvent('cart:updated'));
    // §8.1 trigger: undo is effectively an add-to-cart, chill-tap fires.
    document.dispatchEvent(new CustomEvent('chillandout:cart-added'));
  }

  async _addToCart(formData) {
    if (this._destroyed) return;
    // Optimistic UI — open drawer immediately so customer sees instant feedback.
    // Inline sections for header-group sections always returns null on this store,
    // so we skip that attempt entirely and go straight to _refreshDrawer() which
    // parallel-fetches section HTML + /cart.js (reliable, fast, correct threshold).
    this._setLoading(true);
    this.open();
    formData.delete('sections'); // not used — header-group inline render returns null
    const res = await fetch(`${CART_ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: formData,
    });
    if (!res.ok) {
      this._setLoading(false);
      const errBody = await res.json().catch(() => null);
      throw new Error(errBody?.description || `cart/add failed: ${res.status}`);
    }
    await this._refreshDrawer();
    this._setLoading(false);
    // §8.1 trigger: chill-tap soundscape on every successful cart-add.
    document.dispatchEvent(new CustomEvent('chillandout:cart-added'));
  }

  async _refreshDrawer() {
    // Parallel-fetch section HTML + cart state so we can correct the free-shipping
    // threshold client-side (bypasses Shopify server-side Liquid cache issues).
    const [secRes, cartRes] = await Promise.all([
      fetch(`${CART_ROOT}?sections=${this._sectionId}`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      }),
      fetch(`${CART_ROOT}cart.js`, {
        headers: { 'Accept': 'application/json' },
        credentials: 'same-origin',
      }),
    ]);
    if (!secRes.ok) throw new Error(`section refresh failed: ${secRes.status}`);
    const [secJson, cart] = await Promise.all([
      secRes.json(),
      cartRes.ok ? cartRes.json() : Promise.resolve(null),
    ]);
    if (secJson[this._sectionId]) {
      this._swapBody(secJson[this._sectionId]);
    }
    // Override server-rendered threshold with correct JS-computed value.
    // This runs AFTER _swapBody so the bar is in the DOM.
    if (cart) {
      this._updateFreeShipping(cart.total_price);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
    } else {
      document.dispatchEvent(new CustomEvent('cart:updated'));
    }
  }

  // Recompute free-shipping bar after every AJAX cart update.
  // Both TRY and USD thresholds are stored in separate DOM attributes by Liquid
  // so JS never needs hardcoded values. Active currency selects the right one.
  // Thresholds: TRY ₺2,500 (250000 kuruş) | USD/other $109 (10900 cents)
  _updateFreeShipping(totalPriceCents) {
    const bar = this._body?.querySelector('[data-cart-free-shipping]');
    if (!bar) return;

    // Currency priority: Liquid localization (most reliable, set via
    // localization.country.currency.iso_code) → window.Shopify.currency.active
    // → fallback to Liquid's pre-computed threshold.
    const currency = this.dataset.currency || window.Shopify?.currency?.active;
    let threshold;
    if (currency === 'TRY') {
      threshold = parseInt(this.dataset.freeShippingThresholdTry, 10) || 0;
    } else if (currency) {
      // USD, EUR, GBP etc.
      threshold = parseInt(this.dataset.freeShippingThresholdUsd, 10) || 0;
    } else {
      // Last resort: use Liquid's pre-computed threshold.
      threshold = parseInt(this.dataset.freeShippingThreshold, 10) || 0;
    }
    if (threshold <= 0) return; // bar disabled / threshold not configured
    const total = typeof totalPriceCents === 'number' ? totalPriceCents : 0;
    const isMet = total >= threshold && threshold > 0;
    const progress = threshold > 0 ? Math.min(100, Math.round((total / threshold) * 100)) : 0;
    const remaining = Math.max(0, threshold - total);

    bar.style.setProperty('--cart-free-shipping-progress', `${progress}%`);
    bar.classList.toggle('is-met', isMet);

    const copy = bar.querySelector('.cart-free-shipping__copy');
    if (!copy) return;
    if (isMet) {
      const metText = this.dataset.i18nShippingMet || "Shipping's on us ✦";
      copy.innerHTML = `<strong>${metText}</strong>`;
    } else {
      const template = this.dataset.i18nShippingRemaining || 'CHILL_AMOUNT away from free shipping!';
      copy.innerHTML = template.replace('CHILL_AMOUNT', `<strong>${this._formatMoney(remaining)}</strong>`);
    }
  }

  /* ---------- DOM swap ---------- */

  _swapBody(htmlString, { markEntering = false } = {}) {
    if (!this._body) return;
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    const newBody = doc.querySelector('[data-cart-drawer-body]');
    if (!newBody) return;
    // Remember which keys were already present so we can mark new ones as entering.
    const existingKeys = markEntering
      ? new Set(Array.from(this._body.querySelectorAll('[data-cart-line]')).map((el) => el.dataset.lineKey))
      : null;
    // replaceChildren transfers the already-parsed nodes — no re-serialization
    // through the innerHTML setter. Defense-in-depth against any future case
    // where the swapped HTML might contain attribute-based handlers.
    this._body.replaceChildren(...newBody.childNodes);
    if (existingKeys) {
      this._body.querySelectorAll('[data-cart-line]').forEach((el) => {
        if (!existingKeys.has(el.dataset.lineKey)) {
          el.classList.add('is-entering');
        }
      });
    }
  }

  /* ---------- loading + toast ---------- */

  _setLoading(loading) {
    if (!this._body) return;
    this._body.classList.toggle('is-loading', !!loading);
  }

  // Phase 2 polish: optional action button for undo / retry flows.
  _showToast(message, options) {
    if (!this._panel) return;
    options = options || {};
    // Remove any existing toast first.
    this._panel.querySelectorAll('.cart-drawer__toast').forEach((el) => el.remove());
    const toast = document.createElement('div');
    toast.className = 'cart-drawer__toast';
    toast.setAttribute('role', 'status');

    const messageEl = document.createElement('span');
    messageEl.className = 'cart-drawer__toast-message';
    messageEl.textContent = message;
    toast.appendChild(messageEl);

    if (options.actionLabel && typeof options.onAction === 'function') {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'cart-drawer__toast-action';
      button.textContent = options.actionLabel;
      button.addEventListener('click', () => {
        toast.remove();
        if (this._toastTimer) {
          clearTimeout(this._toastTimer);
          this._toastTimer = 0;
        }
        options.onAction();
      });
      toast.appendChild(button);
    }

    this._panel.appendChild(toast);
    if (this._toastTimer) clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => {
      toast.remove();
      this._toastTimer = 0;
    }, TOAST_DURATION_MS);
  }

  /* ---------- Phase 2: Bridge real recommendations ---------- */

  // Replace server-rendered "first-eligible-from-collection" Bridge with
  // Shopify's complementary recommendation API. Falls back gracefully on
  // any error — server-rendered bridge stays in place.
  async _enhanceBridge() {
    if (this._bridgeEnhanced) return;
    if (!this.hasAttribute('data-bridge-enabled')) return;
    if (!this._body) return;

    // Find the first cart line we can use as a recommendation seed.
    const firstLine = this._body.querySelector('[data-cart-line]');
    if (!firstLine) return;
    const seedProductId = firstLine.dataset.productId;
    if (!seedProductId) return;

    this._bridgeEnhanced = true;

    try {
      const recRes = await fetch(
        `${CART_ROOT}recommendations/products.json?product_id=${encodeURIComponent(seedProductId)}&intent=complementary&limit=4`,
        { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' }
      );
      if (!recRes.ok) return;
      const data = await recRes.json();
      const products = Array.isArray(data.products) ? data.products : [];
      if (products.length === 0) return;

      // Filter out products already in cart.
      const cartProductIds = new Set(
        Array.from(this._body.querySelectorAll('[data-cart-line]'))
          .map((el) => el.dataset.productId)
          .filter(Boolean)
      );
      const candidate = products.find(
        (p) => p && p.available && !cartProductIds.has(String(p.id))
      );
      if (!candidate) return;

      // Replace the bridge product card. Keep the same .cart-bridge wrapper +
      // heading so the visual signature is unchanged — only the inner product
      // card swaps.
      const bridgeSlot = this._body.querySelector('[data-bridge-slot]');
      if (!bridgeSlot) return;

      const productCard = this._buildBridgeCard(candidate);
      const existing = bridgeSlot.querySelector('.cart-bridge__product');
      if (existing) {
        existing.replaceWith(productCard);
      } else {
        bridgeSlot.appendChild(productCard);
      }
    } catch {
      // Network error / Shopify unavailable — server-rendered bridge stays.
    }
  }

  _buildBridgeCard(product) {
    const wrap = document.createElement('div');
    wrap.className = 'cart-bridge__product';

    const mediaLink = document.createElement('a');
    mediaLink.className = 'cart-bridge__media';
    mediaLink.href = product.url;
    mediaLink.tabIndex = -1;
    mediaLink.setAttribute('aria-hidden', 'true');
    if (product.featured_image) {
      const img = document.createElement('img');
      img.className = 'cart-bridge__image';
      img.src = product.featured_image;
      img.alt = product.title || '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 64;
      img.height = 64;
      mediaLink.appendChild(img);
    }
    wrap.appendChild(mediaLink);

    const info = document.createElement('div');
    info.className = 'cart-bridge__info';
    const title = document.createElement('p');
    title.className = 'cart-bridge__title';
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.style.color = 'inherit';
    titleLink.style.textDecoration = 'none';
    titleLink.textContent = product.title || '';
    title.appendChild(titleLink);
    const price = document.createElement('p');
    price.className = 'cart-bridge__price';
    price.textContent = this._formatMoney(product.price);
    info.appendChild(title);
    info.appendChild(price);
    wrap.appendChild(info);

    const cta = document.createElement('a');
    cta.className = 'cart-bridge__cta';
    cta.href = product.url;
    cta.textContent = 'take it home';
    wrap.appendChild(cta);

    return wrap;
  }

  _formatMoney(cents) {
    if (typeof cents !== 'number') return '';
    // Defer to Horizon's money helper if present (handles locale + currency).
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      try {
        const raw = window.Shopify.formatMoney(cents);
        // formatMoney may return HTML like <span class=money>₺2,210</span>.
        // Extract plain text so it renders correctly in both innerHTML and textContent contexts.
        const tmp = document.createElement('span');
        tmp.innerHTML = raw;
        return tmp.textContent || raw;
      } catch { /* fall through */ }
    }
    // Fallback: basic formatting per active currency.
    const currency = window.Shopify?.currency?.active || 'USD';
    const amount = cents / 100;
    if (currency === 'TRY') {
      return '₺' + amount.toFixed(0);
    }
    // Whole dollars/euros etc — drop .00 suffix for cleanliness.
    return '$' + (Number.isInteger(amount) ? amount.toFixed(0) : amount.toFixed(2));
  }

  /* ---------- Phase 2: mobile swipe-down-to-close ---------- */

  _setupSwipeDownToClose() {
    if (this._swipeWired) return;
    if (!this._panel) return;
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    this._swipeWired = true;
    const handle = this.querySelector('[data-cart-drawer-handle]')
      || this.querySelector('.cart-drawer__chrome');
    if (!handle) return;

    const SWIPE_THRESHOLD = 80;
    const SWIPE_RUBBER = 120;
    let state = null;

    const onDown = (e) => {
      if (e.pointerType === 'mouse') return;
      state = { startY: e.clientY, dy: 0, pointerId: e.pointerId, raf: 0 };
      handle.setPointerCapture(e.pointerId);
      this.dataset.swiping = 'true';
    };

    const onMove = (e) => {
      if (!state || e.pointerId !== state.pointerId) return;
      state.dy = Math.max(0, e.clientY - state.startY); // only downward
      if (state.raf) return;
      state.raf = requestAnimationFrame(() => {
        state.raf = 0;
        const clamped = Math.min(SWIPE_RUBBER, state.dy);
        this._panel.style.setProperty('--swipe-y', `${clamped.toFixed(1)}px`);
      });
    };

    const onUp = (e) => {
      if (!state || e.pointerId !== state.pointerId) return;
      const dismissed = state.dy >= SWIPE_THRESHOLD;
      try { handle.releasePointerCapture(e.pointerId); } catch {}
      if (state.raf) cancelAnimationFrame(state.raf);
      delete this.dataset.swiping;
      this._panel.style.removeProperty('--swipe-y');
      state = null;
      if (dismissed) this.close();
    };

    handle.addEventListener('pointerdown', onDown);
    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onUp);
    handle.addEventListener('pointercancel', onUp);
  }

  /* ---------- focus trap, body lock, sibling inert ---------- */

  _handleTabTrap(e) {
    if (!this._panel) return;
    const focusables = Array.from(this._panel.querySelectorAll(FOCUSABLE)).filter(
      (el) => !el.hasAttribute('disabled') && el.offsetParent !== null
    );
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  _lockBody() {
    this._previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
  }

  _unlockBody() {
    document.body.style.overflow = this._previousBodyOverflow ?? '';
    this._previousBodyOverflow = null;
  }

  _setSiblingsInert(inert) {
    // Mark every direct body child except this drawer (and script/style tags) as inert.
    // iOS VoiceOver respects `inert` since 15.4 — better than a custom focus trap.
    //
    // Walk up from `this` to find the direct-body-child ancestor. The cart-drawer
    // may be nested inside header-component (when placed in header-group), so we
    // must exclude the entire ancestor chain — not just `this` — to avoid inert
    // being inherited down to the drawer panel itself.
    let drawerRoot = this;
    while (drawerRoot.parentElement && drawerRoot.parentElement !== document.body) {
      drawerRoot = drawerRoot.parentElement;
    }

    const siblings = Array.from(document.body.children);
    siblings.forEach((el) => {
      if (el === drawerRoot) return;
      if (el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
      if (inert) {
        if (!el.hasAttribute('data-cart-drawer-was-inert')) {
          el.setAttribute('data-cart-drawer-was-inert', el.inert ? 'true' : 'false');
        }
        el.inert = true;
      } else {
        const wasInert = el.getAttribute('data-cart-drawer-was-inert');
        el.inert = wasInert === 'true';
        el.removeAttribute('data-cart-drawer-was-inert');
      }
    });
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}
